import React from 'react';
import Icon from '../../../components/AppIcon';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend } from 'recharts';

const ResultsDashboard = ({ selectedAssessment }) => {
  const performanceData = [
    { range: '90-100%', count: 12, percentage: 26.7 },
    { range: '80-89%', count: 18, percentage: 40.0 },
    { range: '70-79%', count: 8, percentage: 17.8 },
    { range: '60-69%', count: 5, percentage: 11.1 },
    { range: '<60%', count: 2, percentage: 4.4 }
  ];

  const passFailData = [
    { name: 'Passed', value: 40, color: '#10b981' },
    { name: 'Failed', value: 5, color: '#ef4444' }
  ];

  const questionAnalysis = [
    { question: 'Q1', correctRate: 95, avgTime: 45 },
    { question: 'Q2', correctRate: 88, avgTime: 62 },
    { question: 'Q3', correctRate: 72, avgTime: 58 },
    { question: 'Q4', correctRate: 91, avgTime: 41 },
    { question: 'Q5', correctRate: 68, avgTime: 75 }
  ];

  const timeAnalysis = [
    { time: '0-10m', count: 5 },
    { time: '10-20m', count: 18 },
    { time: '20-30m', count: 15 },
    { time: '30-40m', count: 7 }
  ];

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-muted-foreground">Total Attempts</span>
            <Icon name="Users" size={20} color="var(--color-primary)" />
          </div>
          <div className="text-3xl font-bold text-foreground">45</div>
          <div className="text-sm text-success mt-1">↑ 12% from last month</div>
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-muted-foreground">Average Score</span>
            <Icon name="TrendingUp" size={20} color="var(--color-success)" />
          </div>
          <div className="text-3xl font-bold text-foreground">84.2%</div>
          <div className="text-sm text-success mt-1">↑ 5.3% improvement</div>
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-muted-foreground">Pass Rate</span>
            <Icon name="Target" size={20} color="var(--color-success)" />
          </div>
          <div className="text-3xl font-bold text-foreground">88.9%</div>
          <div className="text-sm text-success mt-1">40 passed / 5 failed</div>
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-muted-foreground">Avg Completion Time</span>
            <Icon name="Clock" size={20} color="var(--color-primary)" />
          </div>
          <div className="text-3xl font-bold text-foreground">23 min</div>
          <div className="text-sm text-muted-foreground mt-1">Out of 30 min</div>
        </div>
      </div>
      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Score Distribution */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Score Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={performanceData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="range" stroke="var(--color-muted-foreground)" />
              <YAxis stroke="var(--color-muted-foreground)" />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'var(--color-card)', 
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px'
                }}
              />
              <Bar dataKey="count" fill="var(--color-primary)" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Pass/Fail Ratio */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Pass/Fail Ratio</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={passFailData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100)?.toFixed(0)}%`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {passFailData?.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry?.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Question Analysis */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Question Difficulty Analysis</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={questionAnalysis}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="question" stroke="var(--color-muted-foreground)" />
              <YAxis stroke="var(--color-muted-foreground)" />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'var(--color-card)', 
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px'
                }}
              />
              <Legend />
              <Line type="monotone" dataKey="correctRate" stroke="#10b981" name="Correct Rate %" />
              <Line type="monotone" dataKey="avgTime" stroke="#f59e0b" name="Avg Time (sec)" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Completion Time Distribution */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Completion Time Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={timeAnalysis}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="time" stroke="var(--color-muted-foreground)" />
              <YAxis stroke="var(--color-muted-foreground)" />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'var(--color-card)', 
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px'
                }}
              />
              <Bar dataKey="count" fill="#8b5cf6" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      {/* Individual Results Table */}
      <div className="bg-card border border-border rounded-lg">
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-foreground">Individual Results</h3>
            <button className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium">
              <Icon name="Download" size={16} />
              Export Results
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-medium text-foreground">Employee</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-foreground">Score</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-foreground">Result</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-foreground">Attempt</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-foreground">Time Taken</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-foreground">Date</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-foreground">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {[...Array(10)]?.map((_, index) => (
                <tr key={index} className="hover:bg-muted/50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <span className="text-sm font-medium text-primary">JD</span>
                      </div>
                      <span className="text-sm font-medium text-foreground">John Doe</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm font-semibold text-foreground">
                      {Math.floor(Math.random() * 30) + 70}%
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      Math.random() > 0.2 
                        ? 'bg-green-500/10 text-green-500' :'bg-red-500/10 text-red-500'
                    }`}>
                      {Math.random() > 0.2 ? 'Passed' : 'Failed'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                    {Math.floor(Math.random() * 3) + 1}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                    {Math.floor(Math.random() * 20) + 15} min
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                    12/05/2025
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button className="text-sm text-primary hover:underline">View Details</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ResultsDashboard;